/* eslint-disable @next/next/no-img-element */
'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { imageSrc } from '@/lib/imageSrc';
import { useGetAccessoriesQuery } from '@/redux/api/accessoriesApi';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Skeleton } from '../ui/skeleton';

const Equipments = () => {
  const router = useRouter();
  const [textColors, setTextColors] = useState({});
  const { data, isError, isLoading, isSuccess } =
    useGetAccessoriesQuery(undefined);
  const accessories = data?.body;

  // Function to calculate luminance of an image
  const calculateLuminance = (imageSrc: string, index: number) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // Ensure cross-origin image loading
    img.src = imageSrc;

    img.onload = () => {
      // Create an offscreen canvas to analyze the image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      // @ts-ignore
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // @ts-ignore
      const { data } = ctx.getImageData(0, 0, 1, 1); // Get the pixel at (0,0)

      // Calculate luminance: (0.299*R + 0.587*G + 0.114*B)
      const luminance = 0.299 * data[0] + 0.587 * data[1] + 0.114 * data[2];

      // Determine if the background is dark or light
      const isDark = luminance < 128; // Threshold for dark background

      // Update text color in state
      setTextColors((prevColors) => ({
        ...prevColors,
        [index]: isDark ? 'white' : 'black',
      }));
    };
  };

  // Load luminance check for all images once the component mounts
  useEffect(() => {
    if (isSuccess && accessories.length > 0) {
      accessories?.forEach((data: any, index: number) =>
        calculateLuminance(data.image, index),
      );
    }
  }, [accessories, isSuccess]);

  if (isLoading)
    return (
      <div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-12">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="col-span-6 md:col-span-4 lg:col-span-3">
              <Skeleton className="aspect-[16/9] w-full" />
            </div>
          ))}
        </div>
      </div>
    );

  if (isError) return null;

  return (
    <div className="px-2 py-10 md:px-0">
      <div className="md:ml-[8.5vw]">
        <h5 className="text-left text-[28px] font-semibold leading-[32px] tracking-[0.196px] text-[#6e6e73]">
          <span className="text-[#1d1d1f]"> Our State</span> of the art devices
        </h5>
      </div>
      <div className="group/button py-5 transition-all duration-500 ease-in-out">
        <Carousel
          opts={{
            align: 'center', // Center alignment to mimic Apple Store
            loop: false, // Loop for infinite scroll
          }}
          className="w-full"
        >
          <CarouselContent className="transition-transform duration-500 ease-out">
            {accessories?.map((data: any, index: number) => (
              <CarouselItem
                onClick={() => router.push(`/devices/${data.slug}`)}
                key={index}
                className="transform transition-transform duration-500 ease-in-out md:basis-1/2 md:first:ml-[8.5vw] lg:basis-1/4"
              >
                <div className="relative h-[450px] overflow-hidden rounded-[20px] border shadow-none xl:h-[480px]">
                  <div
                    onClick={() => router.push(`/devices/${data.slug}`)}
                    className="absolute left-0 top-0 z-20 p-[30px]"
                    // @ts-ignore
                    style={{ color: textColors[index] }}
                  >
                    <h5 className="line-clamp-1 cursor-pointer whitespace-normal text-[18px] font-semibold leading-[32px] tracking-[0.196px]">
                      {data.name}
                    </h5>
                  </div>
                  <img
                    src={imageSrc(data.image)}
                    alt={data?.name}
                    className="h-full w-full transform object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                    loading="lazy"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="hidden group-hover/button:block group-hover:block">
            <CarouselPrevious className="left-5" />
            <CarouselNext className="right-5" />
          </div>
        </Carousel>
      </div>
    </div>
  );
};

export default Equipments;
